// Issue #80: エラー時の文言が実態と食い違っていた。
//   (1) catch が原因を問わず「権限がない」と伝えるため、サーバー障害やネットワーク断でも
//       利用者が「自分に権限が無い」と誤解する（問い合わせ先を間違える）。
//   (2) 保存失敗時に DRF のエラーオブジェクトを JSON.stringify した生の文字列を出しており、
//       利用者には読めず、内部の構造もそのまま露出していた。
// 原因が確かなときだけ具体的に伝え、それ以外は原因を断定しない汎用文言にする。

/** 権限不足を伝える定型文（403 のときだけ使う） */
export const PERMISSION_DENIED_MESSAGE =
	"You do not have permission to perform this action.";

/**
 * 通信エラーを利用者向けの一文にする。
 *
 * 403（権限不足）だけは原因が確かなので権限文言を返し、それ以外（サーバー 500・
 * ネットワーク断・タイムアウト等）は呼び出し側が渡す汎用文言を返す。サーバーが返す
 * 本文はそのまま画面へ出さない（内部情報が混ざりうるため）。
 */
export const describeRequestError = (error, fallbackMessage) =>
	error?.response?.status === 403 ? PERMISSION_DENIED_MESSAGE : fallbackMessage;

/**
 * DRF のエラー応答を「項目パス: メッセージ」の組へ再帰的に潰す。
 *
 * 1 段だけの展開では足りない。稼働日数マスタの japan / cairo は
 * `DictField(child=IntegerField(...))`（backend の WorkingDaysMonthField）で、
 * 月ごとの値が範囲外だと DRF は `{japan: {"3": ["..."]}}` のようにキー単位の入れ子を
 * 返す。内側を素朴に String へ渡すと `[object Object]` になり、どの月を直せばよいか
 * 分からなくなる。
 */
const flattenFieldErrors = (value, path) => {
	if (value === null || value === undefined || value === "") {
		return [];
	}

	if (typeof value === "string") {
		return [{ path, text: value }];
	}

	if (typeof value === "number" || typeof value === "boolean") {
		return [{ path, text: String(value) }];
	}

	if (Array.isArray(value)) {
		// 配列は同じ項目に対する複数メッセージなので、パスを増やさずに展開する
		return value.flatMap((item) => flattenFieldErrors(item, path));
	}

	if (typeof value === "object") {
		return Object.entries(value).flatMap(([key, child]) =>
			flattenFieldErrors(child, [...path, key]),
		);
	}

	return [];
};

/**
 * DRF のエラー応答を読める一文へ整形する。
 *
 * 入力の形は一定しない（文字列・配列・{field: [msg]}・{field: {key: [msg]}}・
 * {detail: msg}）ため揃える。解釈できないときは汎用文言へ落とす（生の JSON は出さない）。
 */
export const formatApiErrorDetail = (payload, fallbackMessage) => {
	if (payload === null || payload === undefined || payload === "") {
		return fallbackMessage;
	}

	if (typeof payload === "string") {
		return payload;
	}

	if (typeof payload !== "object") {
		return fallbackMessage;
	}

	// DRF が権限・認証エラー等で返す { detail: "..." } は、そのまま一文として読める
	if (
		!Array.isArray(payload) &&
		typeof payload.detail === "string" &&
		payload.detail
	) {
		return payload.detail;
	}

	// 同じ項目に複数のメッセージが来ることがあるため、パス単位でまとめてから並べる
	const grouped = new Map();
	for (const { path, text } of flattenFieldErrors(payload, [])) {
		const key = path.join(".");
		grouped.set(key, grouped.has(key) ? `${grouped.get(key)} ${text}` : text);
	}

	const parts = Array.from(grouped, ([key, text]) =>
		key ? `${key}: ${text}` : text,
	);

	return parts.length > 0 ? parts.join(" / ") : fallbackMessage;
};
